import React, { useState, useEffect } from 'react';
import { Alert, Badge, Button, Form, Spinner, Stack } from 'react-bootstrap';
import { CheckCircleFill, Lock, PlayFill, Trash } from 'react-bootstrap-icons';
import useUXStore from '../api/ux/store';
import { useAuth } from '../api/auth/store';
import { useSettings, SettingsData, SettingsUpdatePayload } from '../api/settings/store';
import { TestConnectionResponse } from '../api/settings/actions';
import CStack from './CStack';
import EditorControl from './EditorControl';
import FileInputControl from './FileInputControl';
import LabelControl from './LabelControl';
import Loading from './Loading';

const ROLE_OWNER = "global.owner";
const ROLE_ADMIN = "global.admin";

const ensureHostsArray = (data?: Partial<SettingsData>): string[] => {
    if (!data) return [];
    if (Array.isArray(data.hosts)) {
        return data.hosts;
    }
    if (typeof data.host === "string" && data.host.trim().length > 0) {
        return [data.host];
    }
    return [];
};

const sanitizeHosts = (hosts: string[]): string[] =>
    hosts
        .map(host => (typeof host === "string" ? host.trim() : ""))
        .filter(host => host.length > 0);

type HostValidationResult = {
    valid: boolean;
    sanitized: string[];
    message?: string;
};

const validateHosts = (hosts: string[]): HostValidationResult => {
    const sanitized = sanitizeHosts(hosts);
    if (sanitized.length === 0) {
        return {
            valid: false,
            sanitized,
            message: "At least one host is required.",
        };
    }

    const seen = new Set<string>();
    for (const host of sanitized) {
        const normalized = host.toLowerCase();
        if (seen.has(normalized)) {
            return {
                valid: false,
                sanitized,
                message: "Duplicate hosts are not allowed.",
            };
        }
        seen.add(normalized);

        try {
            const parsed = new URL(host);
            if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
                return {
                    valid: false,
                    sanitized,
                    message: "Host must use http:// or https://.",
                };
            }
        } catch {
            return {
                valid: false,
                sanitized,
                message: `Invalid host URL: ${host}`,
            };
        }
    }

    return { valid: true, sanitized };
};

const SettingsDialogContent: React.FC = () => {
    const { updateSettings, retriveSettings, settingsData, testConn } = useSettings(state => state);
    const { addToast, hideDialog } = useUXStore(state => state);
    const { user } = useAuth(state => state);
    const [testingHost, setTestingHost] = useState<string | null>(null);
    const [testResult, setTestResult] = useState<TestConnectionResponse | null>(null);
    const [testedHost, setTestedHost] = useState<string | null>(null);

    const isAdmin = user?.roles?.some(role =>
        typeof role === "string" && (role === ROLE_ADMIN || role === ROLE_OWNER),
    ) || false;

    useEffect(() => {
        retriveSettings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onApply = async (newData: any): Promise<void> => {
        if (!isAdmin) {
            addToast("Permission Denied", "Admin role required to modify settings", null, "danger");
            return;
        }

        const candidateHosts = Array.isArray(newData.hosts) ? newData.hosts : ensureHostsArray(newData);
        const validation = validateHosts(candidateHosts);
        if (!validation.valid) {
            addToast("Invalid Hosts", validation.message ?? "Please provide valid hosts.", null, "warning");
            return;
        }

        const payload: SettingsUpdatePayload = {
            hosts: validation.sanitized,
        };

        if (newData.keyFile && newData.keyFile.length > 0) {
            payload.key = newData.keyFile[0];
        }

        newData.hosts = validation.sanitized;
        newData.host = validation.sanitized[0] ?? "";

        const result = await updateSettings(payload);
        if (result === "gpg_invalid_key") {
            addToast("Settings failed", "Failed verifying user, please provide valid key.", null, "danger");
        } else if (result === "admin_role_required") {
            addToast("Permission Denied", "Admin role required to modify settings", null, "danger");
        } else {
            addToast("Settings updated", "Settings updated successfully.", null, "success");
            hideDialog("settings-dialog");
        }
    };

    const onStartTestConn = async (host: string) => {
        const trimmed = host ? host.trim() : "";
        if (!trimmed) {
            addToast("Invalid Host", "Please enter a host URL before testing", null, "warning");
            return;
        }

        setTestingHost(trimmed);
        setTestResult(null);
        setTestedHost(trimmed);

        try {
            const result = await testConn(trimmed);
            setTestResult(result);
        } catch (error: any) {
            setTestResult({
                success: false,
                statusCode: 0,
                statusText: "Network Error",
                responseTime: 0,
                error: error?.message || "Failed to connect to server",
            });
        } finally {
            setTestingHost(null);
        }
    };

    const renderTestResult = () => {
        if (!testResult) return null;

        const variant = testResult.success ? "success" : "danger";
        const icon = testResult.success ? "✓" : "✗";

        return (
            <Alert variant={variant} className="mt-3">
                <div className="d-flex align-items-center mb-2">
                    <span className="me-2" style={{ fontSize: "1.5em" }}>{icon}</span>
                    <strong>{testResult.success ? "Connection Successful" : "Connection Failed"}</strong>
                </div>

                <div className="small">
                    {testedHost && (
                        <div><strong>Host:</strong> {testedHost}</div>
                    )}
                    <div><strong>Status:</strong> {testResult.statusCode} {testResult.statusText}</div>
                    <div><strong>Response Time:</strong> {testResult.responseTime}ms</div>

                    {testResult.success && testResult.responseData && (
                        <div className="mt-2 p-2 bg-light rounded">
                            <strong>Server Response:</strong>
                            <div className="mt-1">
                                {testResult.responseData.status && (
                                    <div>• Health Status: <span className="text-success">{testResult.responseData.status}</span></div>
                                )}
                                {testResult.responseData.casaStatus && (
                                    <div>• CASA Status: <span className="text-info">{testResult.responseData.casaStatus}</span></div>
                                )}
                                {testResult.responseData.casaTimestamp && (
                                    <div>• CASA Timestamp: {testResult.responseData.casaTimestamp}</div>
                                )}
                                {Object.keys(testResult.responseData)
                                    .filter(key => !["status", "casaStatus", "casaTimestamp"].includes(key))
                                    .map(key => (
                                        <div key={key}>• {key}: {JSON.stringify(testResult.responseData![key])}</div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {testResult.error && (
                        <div className="mt-2">
                            <strong>Error:</strong> {testResult.error}
                        </div>
                    )}
                </div>

                {!testResult.success && (
                    <div className="mt-3">
                        <strong>Troubleshooting Tips:</strong>
                        <ul className="mb-0 mt-1">
                            <li>Check URL format (include http:// or https://)</li>
                            <li>Verify network connectivity</li>
                            <li>Check firewall settings</li>
                            <li>Validate API endpoint availability</li>
                            <li>Ensure correct port number if specified</li>
                        </ul>
                    </div>
                )}
            </Alert>
        );
    };

    if (!settingsData) return <Loading />;

    return (
        <div>
            {!isAdmin && (
                <Alert variant="info" className="d-flex align-items-center">
                    <Lock className="me-2" size={20} />
                    <div>
                        <strong>Read-Only Mode</strong>
                        <div className="small">Admin role required to modify settings. You can view settings but cannot make changes.</div>
                    </div>
                </Alert>
            )}

            <EditorControl
                onApply={onApply}
                defaultData={settingsData}
                readonly={!isAdmin}
            >
                {(data, onChanged, edit) => {
                    const hostList: string[] = Array.isArray(data.hosts) ? data.hosts : ensureHostsArray(data);

                    const handleHostChange = (index: number, value: string) => {
                        const next = hostList.slice();
                        next[index] = value;
                        onChanged("hosts", next);
                    };

                    const handleAddHost = () => {
                        const next = hostList.length > 0 ? hostList.concat("") : [""];
                        onChanged("hosts", next);
                    };

                    const handleRemoveHost = (index: number) => {
                        const next = hostList.filter((_, idx) => idx !== index);
                        onChanged("hosts", next);
                    };

                    const handlePromoteHost = (index: number) => {
                        if (index <= 0) return;
                        const next = hostList.slice();
                        const [selected] = next.splice(index, 1);
                        next.unshift(selected);
                        onChanged("hosts", next);
                    };

                    return (
                        <>
                            <CStack header="Key">
                                {!edit && <LabelControl value={data.status} text="Status" />}
                                {edit && (
                                    <FileInputControl
                                        text="User Verification"
                                        type="file"
                                        id="keyFile"
                                        onChanged={onChanged}
                                    />
                                )}
                            </CStack>

                            <CStack header="Connection">
                                <div className="text-muted small mb-2">
                                    Hosts are tried in order. The first host is considered active and used by default.
                                </div>

                                {hostList.length === 0 && (
                                    <div className="text-muted small mb-2">
                                        No hosts configured yet. Add a host to enable data generation.
                                    </div>
                                )}

                                {hostList.map((host, index) => {
                                    const normalized = typeof host === "string" ? host.trim() : "";
                                    const showActiveBadge = index === 0 && hostList.length > 1;
                                    const isCurrentTesting = testingHost !== null && testingHost === normalized;
                                    const testDisabled = normalized.length === 0 || testingHost !== null;

                                    return (
                                        <Stack
                                            direction="horizontal"
                                            gap={2}
                                            className="mb-2 align-items-center"
                                            key={`host-${index}`}
                                        >
                                            <div className="flex-grow-1 host-row">
                                                {edit ? (
                                                    <Form.Control
                                                        size="sm"
                                                        value={host}
                                                        placeholder="https://api.example.com"
                                                        onChange={e => handleHostChange(index, e.target.value)}
                                                        className="host-row-input"
                                                    />
                                                ) : (
                                                    <span className="text-break host-row-display">{host}</span>
                                                )}
                                                {showActiveBadge && <Badge bg="primary">Active</Badge>}
                                                <div className="host-row-actions">
                                                    <Button
                                                        size="sm"
                                                        variant="outline-secondary"
                                                        className="host-action-btn"
                                                        onClick={() => onStartTestConn(host)}
                                                        disabled={testDisabled}
                                                        aria-label="Test host"
                                                        title="Test host"
                                                    >
                                                        {isCurrentTesting ? (
                                                            <Spinner animation="border" size="sm" />
                                                        ) : (
                                                            <PlayFill size={16} />
                                                        )}
                                                    </Button>
                                                    {edit && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline-danger"
                                                            className="host-action-btn"
                                                            onClick={() => handleRemoveHost(index)}
                                                            disabled={hostList.length <= 1}
                                                            aria-label="Remove host"
                                                            title="Remove host"
                                                        >
                                                            <Trash size={16} />
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                            {edit && index !== 0 && (
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    className="host-promote-btn"
                                                    onClick={() => handlePromoteHost(index)}
                                                    aria-label="Set as active"
                                                    title="Set as active"
                                                >
                                                    <CheckCircleFill size={16} />
                                                </Button>
                                            )}
                                        </Stack>
                                    );
                                })}

                                {edit && (
                                    <Button size="sm" variant="outline-success" onClick={handleAddHost}>
                                        Add Host
                                    </Button>
                                )}

                                {renderTestResult()}
                            </CStack>
                        </>
                    );
                }}
            </EditorControl>
        </div>
    );
};

export const useSettingsDialog = () => {
    const { refreshDialog } = useUXStore(state => state);

    const showSettingsDialog = () => {
        refreshDialog({
            id: "settings-dialog",
            title: "Settings",
            content: <SettingsDialogContent />,
            visible: true,
            onConfirm: undefined,
            onClose: () => {
                // Dialog closed
            },
        }, true);
    };

    return { showSettingsDialog };
};

export default useSettingsDialog;
